import TaskView20 from '@carbon/icons-react/es/task--view/20';
import { Col, Row, Space, Typography } from 'antd';
import { pick } from 'lodash';
import React from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ProjectId, UserType } from 'api/models';
import { Form } from 'components/atoms/form';
import { Radio } from 'components/atoms/radio';
import { Page } from 'components/molecules/page';
import { HeaderWithIconTitle } from 'components/molecules/page/page';
import { toastService } from 'components/molecules/toast';
import { ProductiveHeading06 } from 'components/styles/typography';
import { useCurrentUserProjectAccess } from 'hooks/permissions';
import { useCurrentProject } from 'hooks/use-current-project';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateProjectMutation } from 'queries/project';
import { ConfirmChanges } from './confirm-changes';

const SubHeading = styled.h6`
  ${ProductiveHeading06};
`;

const Text = styled.p`
  white-space: normal;
`;

const PageContent = styled(Page.Content)`
  padding: var(--layout-03);

  > .ant-row {
    padding: var(--spacing-03);
  }
`;

export const ProjectVisibility = ({ setDirty }) => {
  const projectDetails = useCurrentProject();

  // External users cant access this page
  const { user } = useUser();
  const history = useHistory();

  if (user.userType === UserType.External) {
    history.push(`/projects/${projectDetails.projectId}/settings/`);
  }

  const [form] = Form.useForm();
  const { mutate: updateProject, isLoading } = useUpdateProjectMutation(
    ProjectId(projectDetails.projectId),
  );
  const [showSave, setShowSave] = React.useState<boolean>(false);

  const title = useTranslation('project.settings.project_visibility');
  const toastTexts = {
    success: {
      title: useTranslation('changes_saved'),
      message: useTranslation('changes_saved.extra'),
    },
    error: {
      title: useTranslation('error_saving'),
      message: useTranslation('error'),
    },
  };

  const { isProjectAdmin } = useCurrentUserProjectAccess(
    ProjectId(projectDetails.projectId),
  );

  const isReadOnly = !isProjectAdmin;

  const handleSuccess = () => {
    setShowSave(false);
    setDirty(false);
    toastService({
      toastContainer: {},
      ...toastTexts.success,
    }).success();
  };
  const handleCancel = () => {
    setShowSave(false);
    setDirty(false);
    form.resetFields();
  };
  const handleFailure = () => {
    toastService({
      toastContainer: {},
      ...toastTexts.error,
    }).error();
  };

  const handleSubmit = () => {
    const values = form.getFieldsValue(true);
    const payload = pick(values, 'projectId', 'visibility');

    updateProject(payload, {
      onSuccess: handleSuccess,
      onError: handleFailure,
    });
  };

  return (
    <Page>
      <Helmet>{title}</Helmet>
      <Page.Header
        $white
        title={<HeaderWithIconTitle icon={<TaskView20 />} title={title} />}
      />
      <PageContent>
        <Row gutter={32} justify="space-around" align="top">
          <Col span={24}>
            <Form
              form={form}
              layout="vertical"
              initialValues={projectDetails}
              onFinish={handleSubmit}
              onValuesChange={() => {
                setDirty(true);
                setShowSave(true);
              }}
              onFinishFailed={() => setShowSave(false)}
            >
              <Form.Item
                name="visibility"
                initialValue={projectDetails?.visibility}
              >
                <Radio.Group>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Radio value="EVERYONE" disabled={isReadOnly}>
                        <Space direction="vertical" size={0}>
                          <Typography>
                            <SubHeading>
                              <FormattedMessage id="projects.create.team_visibility.everyone" />
                            </SubHeading>
                            <Text>
                              <FormattedMessage id="projects.create.team_visibility.everyone.extra" />
                            </Text>
                          </Typography>
                        </Space>
                      </Radio>
                    </Col>
                    <Col span={24}>
                      <Radio value="MEMBERS" disabled={isReadOnly}>
                        <Space direction="vertical" size={0}>
                          <SubHeading data-cy="project_settings.visiblity.invite_only">
                            <FormattedMessage id="projects.create.team_visibility.invited" />
                          </SubHeading>
                          <Text>
                            <FormattedMessage id="projects.create.team_visibility.invited.extra" />
                          </Text>
                        </Space>
                      </Radio>
                    </Col>
                  </Row>
                </Radio.Group>
              </Form.Item>
              {showSave && (
                <ConfirmChanges
                  isLoading={isLoading}
                  handleCancel={handleCancel}
                />
              )}
            </Form>
          </Col>
        </Row>
      </PageContent>
    </Page>
  );
};
